import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getUserData } from "@/lib/api";
import { ContentItem } from "@/types";
import { useAntiScraping } from "@/hooks/use-anti-scraping";
import { Navigation } from "@/components/Navigation";
import { DnaBackground } from "@/components/DnaBackground";
import { ProtectedVideoPlayer } from "@/components/ProtectedVideoPlayer";
import { QuizComponent, LocalQuizQuestion } from "@/components/QuizComponent";
import { TraitExplanation } from "@/components/TraitExplanation";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Video, FileText, BookOpen, PlayCircle, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface StrengthDetail {
  id: string;
  name: string;
  domain: string;
  description: string;
  full_description: string;
  video_url?: string;
  key_characteristics: string[];
  how_to_apply: string[];
  development_tips: string[];
  blind_spots?: string[];
  quiz_questions: LocalQuizQuestion[];
}

export default function StrengthDetail() {
  const { strengthId } = useParams<{ strengthId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [strength, setStrength] = useState<StrengthDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [quizScore, setQuizScore] = useState<number | null>(null);
  
  // Anti-scraping protection
  const { suspiciousActivity, isBot } = useAntiScraping();
  
  // Content from API via location state
  const [contents, setContents] = useState<ContentItem[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);

  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate("/auth");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  useEffect(() => {
    if (strengthId) {
      loadStrengthDetail();
      loadUserProgress();
      
      // Load contents from location state
      if (location.state?.contents) {
        const contentList = location.state.contents as ContentItem[];
        setContents(contentList);
        if (contentList.length > 0) {
          setSelectedContent(contentList[0]);
        }
      }
    }
  }, [strengthId, location.state]);

  const loadStrengthDetail = async () => {
    try {
      setLoading(true);
      
      // TODO: Replace with actual Supabase query when backend is ready
      // For now, using mock data
      const mockStrength: StrengthDetail = {
        id: strengthId || "",
        name: "Achiever",
        domain: "Executing",
        description: "People exceptionally talented in the Achiever theme work hard and possess a great deal of stamina.",
        full_description: "Your Achiever theme helps explain your drive. Achiever describes a constant need for attainment. You feel as if every day starts at zero. By the end of the day you must achieve something tangible in order to feel good about yourself. And by 'every day' you mean every single day—workdays, weekends, vacations. No matter how much you may feel you deserve a day of rest, if the day passes without some form of achievement, no matter how small, you will feel dissatisfied. You have an internal fire burning inside you. It pushes you to do more, to achieve more. After each accomplishment is reached, the fire dwindles for a moment, but very soon it rekindles itself, forcing you toward the next accomplishment.",
        video_url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", // Sample video
        key_characteristics: [
          "Strong work ethic and stamina",
          "Driven by tangible accomplishments",
          "Feel productive when constantly making progress",
          "Set high standards and work tirelessly to meet them",
          "May feel restless when not working toward goals"
        ],
        how_to_apply: [
          "Set daily, weekly, and monthly goals to maintain your sense of achievement",
          "Track your accomplishments to see your progress over time",
          "Channel your drive into mentoring others who need motivation",
          "Use your productivity to take on challenging projects",
          "Create systems and routines that maximize your efficiency"
        ],
        development_tips: [
          "Learn to celebrate your accomplishments before moving to the next goal",
          "Balance your drive with rest to avoid burnout",
          "Partner with people who can help you set strategic priorities",
          "Use your Achiever theme to inspire and energize your team",
          "Consider roles where your productivity and stamina are valued"
        ],
        blind_spots: [
          "May struggle to relax or take breaks",
          "Can become impatient with others who work at a different pace",
          "Might prioritize quantity over quality if not careful",
          "Risk of burnout from constant push for achievement"
        ],
        quiz_questions: [
          {
            id: "q1",
            question: "What is the primary driver for people with the Achiever strength?",
            options: [
              "Recognition from others",
              "A constant need for attainment and tangible accomplishments",
              "Desire to help others",
              "Need for perfection"
            ],
            correctAnswer: 1,
            explanation: "Achievers are driven by an internal need to accomplish something tangible every day, not primarily by external recognition."
          },
          {
            id: "q2",
            question: "How do Achievers typically feel at the end of an unproductive day?",
            options: [
              "Relaxed and refreshed",
              "Indifferent",
              "Dissatisfied",
              "Anxious about others"
            ],
            correctAnswer: 2,
            explanation: "Achievers feel dissatisfied when a day passes without some form of achievement, no matter how small."
          },
          {
            id: "q3",
            question: "What is a potential blind spot for people with Achiever?",
            options: [
              "Being too cautious",
              "Avoiding challenges",
              "Struggling to relax and risk of burnout",
              "Lack of motivation"
            ],
            correctAnswer: 2,
            explanation: "Achievers may find it difficult to take breaks and rest, which can lead to burnout from their constant drive."
          },
          {
            id: "q4",
            question: "How can Achievers best use their strength in a team setting?",
            options: [
              "By working alone on all tasks",
              "By using their productivity to inspire and energize the team",
              "By delegating all their work",
              "By competing with team members"
            ],
            correctAnswer: 1,
            explanation: "Achievers can leverage their drive and productivity to motivate and energize their team members."
          },
          {
            id: "q5",
            question: "What helps Achievers maintain their sense of accomplishment?",
            options: [
              "Waiting for others to set goals",
              "Working without any structure",
              "Setting daily, weekly, and monthly goals",
              "Only focusing on long-term objectives"
            ],
            correctAnswer: 2,
            explanation: "Setting clear goals at various timeframes helps Achievers maintain their sense of progress and accomplishment."
          }
        ]
      };

      setStrength(mockStrength);
    } catch (error) {
      console.error("Error loading strength:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = () => {
    // Load video progress from localStorage
    const videoData = localStorage.getItem(`video_progress_${strengthId}`);
    if (videoData) {
      const { progress } = JSON.parse(videoData);
      setVideoProgress(progress);
    }

    // Load quiz results from localStorage
    const quizData = localStorage.getItem(`quiz_${strengthId}`);
    if (quizData) {
      const { score } = JSON.parse(quizData);
      setQuizScore(score);
    }
  };

  const handleVideoComplete = () => {
    console.log("Video completed!");
    // TODO: Update Supabase when backend is ready
  };

  const handleQuizComplete = (score: number, passed: boolean) => {
    setQuizScore(score);
    console.log(`Quiz completed with score: ${score}%, passed: ${passed}`);
    // TODO: Update Supabase when backend is ready
  };

  if (!user || loading || !strength) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DnaBackground />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <DnaBackground />
      <Navigation user={user} />
      
      <main className="pt-20 px-4 pb-10 sm:px-6 sm:pt-24 sm:pb-12">
        <div className="container mx-auto max-w-5xl min-w-0">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/assessments")}
            className="mb-6 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Assessments
          </Button>
          {/* Security Alert */}
          {(suspiciousActivity || isBot) && (
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Security Alert: Suspicious activity detected. Video and content access may be restricted.
                If you believe this is an error, please contact support.
              </AlertDescription>
            </Alert>
          )}
          {/* Content Selection - Show if multiple contents */}
          {contents.length > 1 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Available Content</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {contents.map((content) => (
                  <Card
                    key={content._id}
                    onClick={() => setSelectedContent(content)}
                    className={`p-4 cursor-pointer transition-all duration-300 ${
                      selectedContent?._id === content._id
                        ? "bg-primary/20 border-primary shadow-glow"
                        : "bg-card hover:bg-muted border-border"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <Badge variant="outline" className="text-xs">
                        {content.strengthId.domain}
                      </Badge>
                      {content.videoUrl && (
                        <PlayCircle className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {content.shortDescription}
                    </p>
                    <div className="mt-3 flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                      <Badge
                        variant={content.status === "published" ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {content.status}
                      </Badge>
                      <span>{new Date(content.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Tabbed Content */}
            <Tabs defaultValue="video" className="w-full min-w-0">
            <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <TabsList className="grid w-max min-w-full grid-cols-3 mb-8">
              <TabsTrigger value="video" className="gap-2">
                <Video className="w-4 h-4" />
                Video
              </TabsTrigger>
              <TabsTrigger value="explanation" className="gap-2">
                <BookOpen className="w-4 h-4" />
                Explanation
              </TabsTrigger>
              <TabsTrigger value="quiz" className="gap-2">
                <FileText className="w-4 h-4" />
                Quiz {quizScore !== null && `(${quizScore}%)`}
              </TabsTrigger>
            </TabsList>
            </div>

            <TabsContent value="video" className="space-y-6">
              {selectedContent?.videoUrl ? (
                <ProtectedVideoPlayer
                  videoUrl={selectedContent.videoUrl}
                  title={strength.name}
                  strengthName={strength.name}
                  onComplete={handleVideoComplete}
                  initialProgress={videoProgress}
                  enableProtection={true}
                />
              ) : (
                <Card className="p-6 text-center bg-card border-border sm:p-12">
                  <PlayCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="text-xl font-semibold mb-2">No Video Available</h3>
                  <p className="text-muted-foreground">
                    Video content for this strength is not available yet.
                  </p>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="explanation" className="space-y-6">
              {selectedContent ? (
                <Card className="p-4 bg-card border-border sm:p-8">
                  <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <h2 className="text-2xl font-bold mb-2 sm:text-3xl">{strength.name}</h2>
                      <Badge className="text-sm">{selectedContent.strengthId.domain}</Badge>
                    </div>
                  </div>
                  
                  <div className="prose prose-invert max-w-none">
                    <p className="text-lg text-muted-foreground mb-6">
                      {selectedContent.shortDescription}
                    </p>
                    
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {selectedContent.mainContent}
                    </div>
                  </div>
                </Card>
              ) : (
                <TraitExplanation
                  strengthName={strength.name}
                  domain={strength.domain}
                  fullDescription={strength.full_description}
                  keyCharacteristics={strength.key_characteristics}
                  howToApply={strength.how_to_apply}
                  developmentTips={strength.development_tips}
                  potentialBlindSpots={strength.blind_spots}
                />
              )}
            </TabsContent>

            <TabsContent value="quiz" className="space-y-6">
              <QuizComponent
                contentId={selectedContent?._id || strength.id}
                strengthName={strength.name}
                questions={strength.quiz_questions}
                passingScore={70}
                onComplete={handleQuizComplete}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
